import classNames from 'clsx';
import { once } from 'ramda';
import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle } from 'react';
import { useDispatch } from 'react-redux';
import {
    AI_VIEWER_TOOLBAR_ID,
    isAiImprovePromoTooltipViewerEnabled,
    isAiImprovePromoViewerEnabled,
} from 'reactApp/appHelpers/featuresHelpers/features/aiPhotoImprove';
import { isAiRmvPromoTooltipViewerEnabled, isAiRmvPromoViewerEnabled } from 'reactApp/appHelpers/featuresHelpers/features/imageTools';
import { VK_UI_DARK_THEME } from 'reactApp/constants';
import { aiToolsCompareImage, improveImageResultAction, improveImageSave } from 'reactApp/modules/aiTools/aiTools.module';
import type { AiToolsFeatures } from 'reactApp/modules/aiTools/aiTools.types';
import { updatePromoManager } from 'reactApp/modules/promoNew/manager';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { AddBackgroundButton } from 'reactApp/ui/ReactViewer/AiImprove/AddBackgroundButton';
import { sendRadar, useImproveImageHandlers } from 'reactApp/ui/ReactViewer/AiImprove/hooks';
import { RatingBlock } from 'reactApp/ui/ReactViewer/AiImprove/RatingBlock';
import type { controlNames, IViewerManage } from 'reactApp/ui/ReactViewer/ReactViewer.types';

import { renderDiscardChanges } from './AiImprovePopups';
import styles from './AiImproveToolbar.css';
import { CancelButton, CompareButton, Delimiter, ImproveButton, ProgressButton, RemoveBackgroundButton, SaveButton } from './Buttons';

interface Props extends AiToolsFeatures {
    id: string;
    ext: string;
    size: number;
    storage?: EStorageType | null;
}

const updatePromoManagerOnce = once(() => updatePromoManager());

export const AiImproveToolbar = memo(
    forwardRef<IViewerManage, Props>(function AiImproveToolbarFunc({ id, ext, storage, improve, removeBackground }, ref) {
        const dispatch = useDispatch();

        const {
            handleImproveClick,
            handleRemoveBackgroundClick,
            availableCount,
            availableCountRmvBgrnd,
            isDone,
            isPaymium,
            isCompare,
            isProcessing,
            endLimitTime,
            endLimitTimeRmvBgrnd,
            type,
            isNewbackground,
        } = useImproveImageHandlers();

        useEffect(() => {
            if (
                isAiImprovePromoViewerEnabled ||
                isAiImprovePromoTooltipViewerEnabled ||
                isAiRmvPromoTooltipViewerEnabled ||
                isAiRmvPromoViewerEnabled
            ) {
                updatePromoManagerOnce();
            }
        }, []);

        useEffect(() => {
            sendRadar(storage);
            if (removeBackground) {
                sendRadar(storage, 'show', '', false);
            }
        }, [storage, removeBackground]);

        const handleBeforeUnload = useCallback(
            (event) => {
                if (isDone) {
                    event.preventDefault();
                }
            },
            [isDone]
        );

        useEffect(() => {
            window.addEventListener('beforeunload', handleBeforeUnload);

            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
            };
        }, [handleBeforeUnload]);

        const handleSave = useCallback(
            (saveAsNew = false, closeViewer = false) => {
                sendRadar(storage, 'click', saveAsNew ? 'savenew' : 'save', type !== 'removeBackground');
                dispatch(improveImageSave({ id, storage, saveAsNew, ext, closeViewer }));
            },
            [dispatch, id, ext, storage, type]
        );

        useImperativeHandle(
            ref,
            (): IViewerManage => ({
                disableNavigation: () => isDone,
                disableClose: (closeViewer) => {
                    if (!isDone) {
                        return false;
                    }

                    renderDiscardChanges({
                        type,
                        isPaymium,
                        endLimitTime: type === 'removeBackground' ? endLimitTimeRmvBgrnd : endLimitTime,
                        availableCount: type === 'removeBackground' ? availableCountRmvBgrnd : availableCount,
                        isDiscardFlow: false,
                        onDiscardClick: () => {
                            dispatch(improveImageResultAction({ id, storage, result: 'cancel', ext }));
                            // Чтобы диспатч выше прилетел в редакс стор
                            queueMicrotask?.(closeViewer);
                        },
                        onSaveClick() {
                            handleSave(false, true);
                        },
                    });

                    return true;
                },
                handleClick(name: controlNames) {
                    if (name === 'improve') {
                        handleImproveClick();
                    }
                },
            }),
            [
                isDone,
                type,
                isPaymium,
                endLimitTimeRmvBgrnd,
                endLimitTime,
                availableCount,
                availableCountRmvBgrnd,
                dispatch,
                id,
                storage,
                ext,
                handleSave,
                handleImproveClick,
            ]
        );

        const handleCompare = useCallback(() => {
            sendRadar(storage, 'click', 'compare');
            dispatch(aiToolsCompareImage({ id, storage, compareRegime: !isCompare, ext }));
        }, [dispatch, id, ext, isCompare, storage]);

        const handleDiscard = useCallback(() => {
            sendRadar(storage, 'click', 'discard', type !== 'removeBackground');
            return renderDiscardChanges({
                isPaymium,
                endLimitTime: type === 'removeBackground' ? endLimitTimeRmvBgrnd : endLimitTime,
                availableCount: type === 'removeBackground' ? availableCountRmvBgrnd : availableCount,
                type,
                isDiscardFlow: true,
                onDiscardClick: () => {
                    dispatch(improveImageResultAction({ id, ext, storage, result: 'cancel' }));
                },
            });
        }, [storage, type, isPaymium, endLimitTimeRmvBgrnd, endLimitTime, availableCount, availableCountRmvBgrnd, dispatch, id, ext]);

        const showCompare = isDone && type === 'improve';
        const showImprove = !isDone && !isProcessing && improve;
        const showRemove = !isDone && !isProcessing && removeBackground;

        return (
            <div className={classNames(styles.root, VK_UI_DARK_THEME)} id={AI_VIEWER_TOOLBAR_ID} data-qa-id={AI_VIEWER_TOOLBAR_ID}>
                <div
                    className={classNames(styles.controls, {
                        [styles.controls_primary]: !isDone && !isProcessing,
                        [styles.primaryHover]: showRemove && showImprove,
                    })}
                >
                    {showImprove && <ImproveButton isProcessing={isProcessing} onClick={handleImproveClick} data-qa-id="bttn-improve" />}
                    {showImprove && showRemove && <Delimiter />}
                    {showRemove && (
                        <RemoveBackgroundButton
                            isProcessing={isProcessing}
                            onClick={handleRemoveBackgroundClick}
                            data-qa-id="bttn-rmv-back"
                        />
                    )}
                    {isProcessing && <ProgressButton type={type} />}
                    {isDone && (
                        <>
                            <RatingBlock type={type} />
                            <CancelButton onClick={handleDiscard} data-qa-id="bttn-cancel" type={type} isNewbackground={isNewbackground} />
                            <Delimiter />
                            {showCompare && (
                                <>
                                    <CompareButton onClick={handleCompare} active={isCompare} data-qa-id="bttn-compare" />
                                    <Delimiter />
                                </>
                            )}
                            {type === 'removeBackground' && (
                                <>
                                    <AddBackgroundButton pathId={id} isNewbackground={isNewbackground} />
                                    <Delimiter />
                                </>
                            )}
                            <SaveButton
                                type={type}
                                onClick={handleSave}
                                showSaveVariants={storage !== EStorageType.public && storage !== EStorageType.viewerAttaches}
                            />
                        </>
                    )}
                </div>
            </div>
        );
    })
);

AiImproveToolbar.displayName = 'AiImproveToolbar';
